import dayjs from "dayjs";
import React, { useState } from "react";
import { DateRangePicker } from "rsuite";
import styled, { css } from "styled-components";

import Fab from "@mui/material/Fab";
import { useAppDispatch } from "@hooks/store";
import { setRange } from "@store/slices/stats";
import { localeIsoDateRange } from "@utils/date";
import { device } from "@scss/constants/mediaQuery";
import { predefinedRanges } from "@constants/calendarSliding";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import type { FC } from "react";
import type { DateRange } from "rsuite/DateRangePicker";
import type { DateRange as Range } from "@interfaces/stats";

import "rsuite/dist/rsuite-no-reset.min.css";

type Step = "left" | "right";
type WeekSliderProps = { range: Range };

const WeekSlider: FC<WeekSliderProps> = (props) => {
  const dispatch = useAppDispatch();

  const range: DateRange = [
    new Date(props.range.start),
    new Date(props.range.end),
  ];

  const [open, setOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | null>(range);

  const onDateTimeChange = (range: DateRange | null) => {
    setDateRange(range);
    setAppStateDateRange(range as DateRange);
  };

  const onSliderChange = (step: Step) => {
    let range = null;

    const [start, end] = dateRange!;

    const startOfWeek = dayjs(start).startOf("week");
    const endOfWeek = dayjs(end).endOf("week");

    if (step === "right") {
      const nextWeekStart = new Date(startOfWeek.add(1, "week").toISOString());
      const nextWeekEnd = new Date(endOfWeek.add(1, "week").toISOString());

      range = [nextWeekStart, nextWeekEnd];
    }

    if (step === "left") {
      const prevWeekStart = new Date(
        startOfWeek.subtract(1, "week").toISOString(),
      );
      const prevWeekEnd = new Date(endOfWeek.subtract(1, "week").toISOString());

      range = [prevWeekStart, prevWeekEnd];
    }

    setDateRange(range as DateRange);
    setAppStateDateRange(range as DateRange);
  };

  const handleCalendarOpenClose = () => {
    setOpen(!open);
  };

  const setAppStateDateRange = (dateRange: DateRange) => {
    const start = dateRange![0].toISOString();
    const end = dateRange![1].toISOString();

    dispatch(setRange({ start, end }));
  };

  return (
    <Wrapper>
      <Fab
        color="primary"
        aria-label="back"
        size="small"
        onClick={() => onSliderChange("left")}
      >
        <StyledBackIcon />
      </Fab>

      <StyledDateRangePicker
        size="lg"
        open={open}
        value={dateRange}
        showHeader={false}
        onOk={handleCalendarOpenClose}
        ranges={predefinedRanges}
        onChange={onDateTimeChange}
      />

      <StyledFab
        variant="extended"
        onClick={handleCalendarOpenClose}
        sx={{
          mr: 2,
          ml: 2,
          borderRadius: "5px",
          backgroundColor: "white",
        }}
      >
        <SliderTitle>
          <div>Calendar week sliding</div>
          <SliderDateRange>{localeIsoDateRange(dateRange!)}</SliderDateRange>
        </SliderTitle>
      </StyledFab>

      <Fab
        color="primary"
        aria-label="forward"
        size="small"
        onClick={() => onSliderChange("right")}
      >
        <ArrowForwardIosIcon />
      </Fab>
    </Wrapper>
  );
};

const StyledFab = styled(Fab)`
  width: calc(100% - 125px) !important;

  white-space: nowrap;
  text-wrap: nowrap;

  font-size: 8pt !important;
  position: relative;
  left: -3px;

  ${device.mobileL(css`
    font-size: 10pt !important;
  `)}
`;

const StyledDateRangePicker = styled(DateRangePicker)`
  width: 0 !important;
  height: 60px !important;

  position: relative;
  visibility: hidden;

  top: 7px;

  ${device.mobileL(css`
    left: 20px;
  `)}
`;

const StyledBackIcon = styled(ArrowBackIosIcon)`
  position: relative;
  right: -4px;
`;

const SliderTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const SliderDateRange = styled.span`
  color: gray;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
  width: 100%;

  position: relative;

  margin: 0 auto 16px;

  ${device.tablet(css`
    width: 80%;
    height: 40%;
    margin-bottom: 0;
  `)}
`;

export default WeekSlider;
